import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../lib/auth/auth-context';
import { setPasswordSchema, type SetPasswordInput } from '../lib/auth/schemas';
import { friendlyAuthError } from '../lib/auth/errors';
import { supabase } from '../lib/supabase';
import { AuthShell, ErrorAlert } from '../components/auth/AuthShell';
import { Field } from '../components/ui/Field';
import { Button } from '../components/ui/Button';

export const Route = createFileRoute('/signup')({
  component: SignupPage,
});

// Signup flow notes — see PR description for the full reasoning.
//
// Provisioning is invitation-only. A pharmacy_access row in the database
// references public.users.id (FK), which itself references auth.users.id.
// The chain only exists if an admin pre-provisioned the account, which in
// turn means the auth.users row is created by the admin path (Supabase
// invite link), NOT by self-serve signUp.
//
// This page therefore acts as the "accept your invitation" surface:
//   - The invite email's link puts the user into a session via Supabase's
//     hash-token handoff (handled by supabase-js automatically).
//   - We require an active session; if there isn't one, we tell the user
//     to ask their admin for an invite.
//   - We additionally verify a pharmacy_access row exists for the
//     authenticated user (or that they're a platform_admin). RLS allows a
//     user to read their own pharmacy_access rows, so this is enforced
//     client-side as a UX filter and at the DB layer as a true gate.

type GateState =
  | { kind: 'checking' }
  | { kind: 'no_session' }
  | { kind: 'no_access' }
  | { kind: 'ready'; email: string };

function useInviteGate(): GateState {
  const { isReady, session, userType } = useAuth();
  const [state, setState] = useState<GateState>({ kind: 'checking' });

  useEffect(() => {
    let cancelled = false;
    if (!isReady) {
      setState({ kind: 'checking' });
      return;
    }
    if (!session) {
      setState({ kind: 'no_session' });
      return;
    }
    // Platform admins are LYN Rx staff and intentionally have no
    // pharmacy_access rows; they don't need the access check.
    if (userType === 'platform_admin') {
      setState({ kind: 'ready', email: session.user.email ?? '' });
      return;
    }

    void (async () => {
      const { data, error } = await supabase
        .from('pharmacy_access')
        .select('id')
        .eq('user_id', session.user.id)
        .limit(1);
      if (cancelled) return;
      if (error || !data || data.length === 0) {
        setState({ kind: 'no_access' });
        return;
      }
      setState({ kind: 'ready', email: session.user.email ?? '' });
    })();

    return () => {
      cancelled = true;
    };
  }, [isReady, session, userType]);

  return state;
}

function SignupPage(): JSX.Element {
  const gate = useInviteGate();
  const { setPassword, signOut } = useAuth();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SetPasswordInput>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  if (gate.kind === 'checking') {
    return (
      <AuthShell title="Activate your account">
        <p className="text-sm text-ink-2">Loading your invitation…</p>
      </AuthShell>
    );
  }

  if (gate.kind === 'no_session') {
    return (
      <AuthShell
        title="Invitation required"
        subtitle="Sign-up is invitation-only."
        footer={
          <Link to="/login" className="text-primary hover:underline">
            Back to sign in
          </Link>
        }
      >
        <p className="text-sm text-ink-2">
          Open the invitation link your pharmacy admin sent you. If the link expired, ask your admin
          to resend it.
        </p>
      </AuthShell>
    );
  }

  if (gate.kind === 'no_access') {
    return (
      <AuthShell
        title="Account not ready"
        footer={
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => {
              void signOut().then(() => navigate({ to: '/login' }));
            }}
          >
            Sign out and return to login
          </button>
        }
      >
        <p className="text-sm text-ink-2">
          Your account isn&apos;t yet attached to a pharmacy. Contact your pharmacy admin to be
          assigned access.
        </p>
      </AuthShell>
    );
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    const { error } = await setPassword(values.password);
    if (error) {
      setSubmitError(friendlyAuthError('set-password', error));
      return;
    }
    void navigate({ to: '/' });
  });

  return (
    <AuthShell
      title="Set your password"
      subtitle={`Activating ${gate.email}`}
      footer={
        <Link to="/login" className="text-primary hover:underline">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {submitError ? <ErrorAlert>{submitError}</ErrorAlert> : null}
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          autoFocus
          hint="At least 12 characters."
          error={errors.password?.message}
          {...register('password')}
        />
        <Field
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />
        <Button type="submit" loading={isSubmitting} fullWidth>
          Activate account
        </Button>
      </form>
    </AuthShell>
  );
}
